import { Fragment, useMemo, useRef, useState } from 'react'
import { RESUMED_QUIETLY_MS, fingerprint, useToolProgress } from './progress'
import { useI18n } from '@/i18n/I18nProvider'
import { localizeHref } from '@/i18n/href'
import LeadGate from './LeadGate'
import { band } from './stepBands'
import type { SubmitState, ToolResult } from './submit'

/**
 * The Agentic Readiness Index — six dimensions, eighteen questions.
 *
 * The largest of the three tools. Read the header of `content/tools/ari.ts`
 * first: the two sides (Hub, what the business has authorised; Core, what the
 * systems can do) are the whole point, and the archetype is the pair rather than
 * a single score.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS PORT KEPT AND WHAT IT DROPPED.
 *
 * Kept: the scoring exactly (options worth 1–5, dimension = mean of three, side
 * = mean of three dimensions, archetype split at 2.5), the ladder, the
 * archetype prose, the routing onto Digitopia's three offerings, and the
 * "context" step — sector and core systems. That last one is friction before the
 * questions, and it stays because sector plus "which systems do you actually
 * run" is the most useful thing a salesperson can know before the call.
 *
 * Dropped: the source's own lead form and its `/api/ari-lead` fetch, which was
 * a commented-out integration point that only ever `console.log`ged. All three
 * tools go through the shared `LeadGate` and the one write path.
 * ---------------------------------------------------------------------------
 */

const round1 = (n: number) => Math.round(n * 10) / 10

/**
 * Where a routed offering actually lives on the site.
 *
 * Asked for on 2026-08-31: the report names three offerings of ours and left the
 * reader to go and find them in the menu. Keyed by the `solutions` key rather
 * than by the name, because the names are translated per locale and the keys are
 * not — and the value is the English path, which `localizeHref()` rewrites for
 * whoever is reading.
 */
const SOLUTION_PAGES: Record<string, string> = {
  strategy: '/en/solutions/strategy-consulting',
  tmo: '/en/solutions/transformation-management',
  enterprise: '/en/solutions/enterprise-ai-solutions',
}

type Side = 'hub' | 'core'

/**
 * Sector, as a list of ours rather than a native `<select>`.
 *
 * Asked for on 2026-08-30: a list that opens here should show ten options and
 * scroll for the rest. The browser's own popup is the browser's — twenty-one
 * sectors open as a panel over most of the page and no CSS of ours can reach
 * its height. So this is the shape the contact form and the ROI calculator
 * already settled on: ten rows at `py-2 text-sm` is `max-h-[22.5rem]`, and
 * everything past the tenth scrolls inside the panel.
 *
 * The panel is **positioned, not in the flow**. In the flow it pushed the rest
 * of the step down the page every time it opened, which reads as the form
 * coming apart under the pointer; a question must look identical before and
 * after you touch it. It floats on its own layer instead, and closes on Escape
 * or a click anywhere outside — this tool always hydrates, so it can be closed
 * again, which is the condition the contact form's script-free picker cannot
 * meet.
 */
function SectorPicker({
  label,
  placeholder,
  value,
  options,
  onChange,
}: {
  label: string
  placeholder: string
  value: string
  options: readonly string[]
  onChange: (v: string) => void
}) {
  const [open, setOpen] = useState(false)

  return (
    <div
      className={'relative mt-8 block ' + (open ? 'z-20' : '')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
      }}
    >
      <span className="text-sm font-semibold text-foreground">{label}</span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="mt-2 flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-background px-4 py-3 text-left text-base text-foreground focus:border-foreground focus:outline-none"
      >
        <span className={'truncate ' + (value ? '' : 'text-muted-foreground')}>
          {value || placeholder}
        </span>
        <span aria-hidden className="text-xs">▾</span>
      </button>
      {/* Closes the panel on a click anywhere else, without a document listener
          — and, being fixed, it never adds to the page's own height. */}
      {open && (
        <div
          aria-hidden
          className="fixed inset-0 z-10 cursor-default"
          onClick={() => setOpen(false)}
        />
      )}
      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-[22.5rem] overflow-y-auto rounded-md border border-border bg-card py-1 shadow-lg"
        >
          {/* The placeholder is an answer of its own — "prefer not to say" is
              a real thing to tell us, and the field is not required. */}
          {['', ...options].map((o) => (
            <button
              key={o || '__none'}
              type="button"
              role="option"
              aria-selected={o === value}
              onClick={() => {
                onChange(o)
                setOpen(false)
              }}
              className={
                'flex w-full cursor-pointer items-center px-4 py-2 text-left text-sm hover:bg-sidebar-accent '
                + (o === value ? 'bg-sidebar-accent font-medium ' : '')
                + (o ? '' : 'text-muted-foreground')
              }
            >
              {o || placeholder}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Ari() {
  const { t, locale } = useI18n()
  const a = t.tools.ari
  const ui = t.tools.ui

  const openedAt = useRef(Date.now()).current

  const [phase, setPhase] = useState<'intro' | 'context' | 'quiz' | 'gate' | 'report'>('intro')
  const [sector, setSector] = useState('')
  const [systems, setSystems] = useState<string[]>([])
  /** Keyed `dimIndex.questionIndex`, holding the option index. */
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [step, setStep] = useState(0)
  const [saved, setSaved] = useState<SubmitState>({ status: 'idle' })
  /** Set by pressing Next with nothing chosen — see the note beside the button. */
  const [needAnswer, setNeedAnswer] = useState(false)

  /** Flattened question order, so "question 7 of 18" is a single counter. */
  const flat = useMemo(
    () =>
      a.dims.flatMap((d, di) =>
        d.questions.map((q, qi) => ({ d, di, q, qi, key: `${di}.${qi}` })),
      ),
    [a.dims],
  )

  /*
    HALF-FINISHED ANSWERS SURVIVE A REFRESH — in this browser only. See
    `tools/progress.ts` for why this is not a row in our database.

    Eighteen questions across six dimensions plus a context step: this is the
    tool where losing progress costs the most, and the one Fatih was describing.

    THE SECTOR AND THE SYSTEMS ARE KEPT TOO, and they are the most identifying
    thing here — "manufacturing, we run SAP" narrows a company considerably. That
    is precisely why the storage decision matters: it stays in the reader's
    browser and never reaches us, so nothing about it is ours to hold. Do not
    move it server-side without reopening that decision.

    The fingerprint is each dimension's `id` and its questions' option counts.
    `id` is a stable identifier rather than prose, so this survives translation
    and rewording while still discarding progress when the instrument itself
    changes shape — and unlike the Guts Check it also catches a reordering,
    because the ids are part of the hash.
  */
  const progress = useToolProgress({
    tool: 'agentic-readiness-index',
    shape: fingerprint(a.dims.map((d) => [d.id, d.questions.map((q) => q.opts.length)])),
    state: useMemo(() => ({ step, answers, sector, systems }), [step, answers, sector, systems]),
    active: phase === 'context' || phase === 'quiz' || phase === 'gate',
    restore: (saved, ageMs) => {
      // Untrusted. Every answer is checked against the live question set by key,
      // and anything that does not match is dropped rather than coerced — a
      // stored key for a question that no longer exists must not become a score.
      if (!saved || typeof saved !== 'object') return
      const valid: Record<string, number> = {}
      if (saved.answers && typeof saved.answers === 'object') {
        for (const { key, q } of flat) {
          const v = (saved.answers as Record<string, unknown>)[key]
          if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < q.opts.length) {
            valid[key] = v
          }
        }
      }
      const keptSector =
        typeof saved.sector === 'string' && a.sectors.includes(saved.sector) ? saved.sector : ''
      const keptSystems = Array.isArray(saved.systems)
        ? saved.systems.filter((x): x is string => typeof x === 'string' && a.systems.includes(x))
        : []

      // Nothing worth restoring — do not strand the reader on a resume offer
      // that would put them back exactly where a fresh start would.
      if (Object.keys(valid).length === 0 && !keptSector && keptSystems.length === 0) return

      setAnswers(valid)
      setSector(keptSector)
      setSystems(keptSystems)
      setStep(
        typeof saved.step === 'number'
          ? Math.min(Math.max(Math.trunc(saved.step), 0), Math.max(flat.length - 1, 0))
          : 0,
      )
      if (ageMs < RESUMED_QUIETLY_MS) {
        // Back into the questions if they had reached them, otherwise the
        // context step they were still filling in.
        setPhase(Object.keys(valid).length > 0 ? 'quiz' : 'context')
      }
    },
  })

  const scored = useMemo(() => {
    // Options are worth their index PLUS ONE, and an unanswered question
    // defaults to 1 — kept from the source so a dimension score is always on
    // the same 1–5 scale as the ladder.
    const dims = a.dims.map((d, di) => {
      const vals = d.questions.map((_, qi) => (answers[`${di}.${qi}`] ?? 0) + 1)
      return { ...d, di, vals, score: round1(vals.reduce((x, y) => x + y, 0) / vals.length) }
    })
    const mean = (xs: number[]) => round1(xs.reduce((x, y) => x + y, 0) / xs.length)
    const sideScore = (side: Side) => mean(dims.filter((d) => d.side === side).map((d) => d.score))
    const hub = sideScore('hub')
    const core = sideScore('core')
    const all = dims.flatMap((d) => d.vals)
    const index = round1(all.reduce((x, y) => x + y, 0) / all.length)

    const arche =
      hub >= 2.5 && core < 2.5
        ? 'ambition'
        : hub < 2.5 && core >= 2.5
          ? 'capability'
          : hub < 2.5 && core < 2.5
            ? 'observer'
            : 'compounding'

    // Weakest three, ties broken by declaration order so the report is stable.
    const lowest = [...dims].sort((x, y) => x.score - y.score || x.di - y.di).slice(0, 3)

    // Every component the reader put at the bottom of the scale. These are the
    // sharpest thing in the report: not "you scored low" but "this specific
    // thing does not exist".
    const blind = dims.flatMap((d) =>
      d.questions.flatMap((q, qi) => (d.vals[qi] === 1 ? [{ component: q.component, dim: d.title }] : [])),
    )

    return { dims, hub, core, index, arche, lowest, blind }
  }, [a.dims, answers])

  const rung = (v: number) => a.ladder[Math.min(4, Math.max(0, Math.round(v) - 1))]
  const A = a.archetypes[scored.arche as keyof typeof a.archetypes]

  const result: ToolResult = useMemo(
    () => ({
      // The CRM gets the WEAKER SIDE, not the overall index — the same choice
      // the Diagnostic makes, and for the same reason.
      //
      // The overall mean is a real output of this tool and it is on the page as
      // the third box. But as the single sortable number in a CRM list it lies
      // in exactly the way this tool exists to prevent: an Ambition-Led
      // organisation (hub 5.0, core 1.0) means 50 — mid-table, beside a
      // genuinely balanced one, with the constraint that will cause the incident
      // averaged away. `min(hub, core)` says "one side of this is on the floor".
      //
      // Rescaled 1–5 → 0–100 so all three tools are comparable in one column.
      // Every honest 1–5 figure, index included, is in `dimensions`.
      score: Math.round(((Math.min(scored.hub, scored.core) - 1) / 4) * 100),
      verdict: A.name,
      band: scored.arche,
      dimensions: {
        ...Object.fromEntries(scored.dims.map((d) => [d.title, d.score])),
        Hub: scored.hub,
        Core: scored.core,
        Index: scored.index,
      },
      answers: {
        // Context first: sector and the systems they run is the most useful
        // thing on this payload for whoever makes the call.
        Sector: sector || null,
        'Core systems': systems.length ? systems.join(', ') : null,
        ...Object.fromEntries(
          flat.map(({ d, q, key }) => [
            `${d.title} — ${q.component}`,
            answers[key] === undefined ? null : q.opts[answers[key]],
          ]),
        ),
      },
    }),
    [scored, A, sector, systems, flat, answers],
  )

  // ---- intro -------------------------------------------------------------
  if (phase === 'intro') {
    // Wider than the other phases, and that is what pays for the larger type:
    // asked on 2026-08-26 for a shorter grey band with bigger words in it, and
    // those pull in opposite directions at a 3xl measure — 18px in a 372px
    // column simply wraps to more lines and the band grows taller. At 4xl each
    // card is ~430px, so the same sentences occupy about the same number of
    // lines one step up the scale, and the height comes off the padding instead.
    return (
      <div className="mx-auto max-w-4xl">
        {/*
          The button leads and the explanation follows, at a size above the
          site's standard pill — asked for on 2026-08-26. It used to sit under
          the two cards, a screen below the headline, so the page opened with
          reading rather than with the one thing a visitor came to do.
        */}
        <div className="text-center">
          <button className="btn-primary px-12 py-5 text-xl" onClick={() => setPhase('context')}>
            {ui.start}
          </button>
          <p className="mt-4 text-base text-muted-foreground">
            {ui.questionCount.replace('{n}', String(flat.length))}
          </p>
          {/* Only a return visit reaches this — a refresh was already restored. */}
          {progress.resumedAt !== null && (
            <div className="mt-6">
              <p className="text-base text-muted-foreground">{ui.resumeNote}</p>
              <button
                className="mt-2 underline"
                onClick={() => setPhase(Object.keys(answers).length > 0 ? 'quiz' : 'context')}
              >
                {ui.resume}
              </button>
            </div>
          )}
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {(['hub', 'core'] as Side[]).map((side) => (
            <div key={side} className="rounded-2xl border border-border p-6">
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {side === 'hub' ? a.ui.hubLabel : a.ui.coreLabel}
              </p>
              <p className="mt-2 text-lg leading-relaxed text-muted-foreground">
                {side === 'hub' ? a.ui.hubBlurb : a.ui.coreBlurb}
              </p>
              <ul className="mt-4 space-y-2">
                {a.dims
                  .filter((d) => d.side === side)
                  .map((d) => (
                    <li key={d.id} className="text-base font-medium text-foreground">
                      {d.title}
                      <span className="font-normal text-muted-foreground"> — {d.sub}</span>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ---- context -----------------------------------------------------------
  if (phase === 'context') {
    return (
      <div className="mx-auto max-w-2xl">
        <h3 className="text-2xl font-bold tracking-tight text-foreground">{a.ui.contextHeading}</h3>
        <p className="mt-2 text-base leading-relaxed text-muted-foreground">{a.ui.contextSub}</p>

        <SectorPicker
          label={a.ui.sectorLabel}
          placeholder={a.ui.sectorPlaceholder}
          value={sector}
          options={a.sectors}
          onChange={setSector}
        />

        <p className="mt-8 text-sm font-semibold text-foreground">{a.ui.systemsLabel}</p>
        <p className="mt-1 text-sm text-muted-foreground">{a.ui.systemsSub}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {a.systems.map((s) => {
            const on = systems.includes(s)
            return (
              <button
                key={s}
                onClick={() => setSystems((xs) => (on ? xs.filter((x) => x !== s) : [...xs, s]))}
                className={
                  'rounded-full border px-4 py-2 text-sm font-medium transition-colors [transition-duration:var(--transition-base)] ' +
                  (on
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border text-muted-foreground hover:border-foreground/40')
                }
              >
                {s}
              </button>
            )
          })}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <button className="btn-secondary" onClick={() => setPhase('intro')}>
            {ui.back}
          </button>
          {/* Deliberately not required. Somebody who will not name their sector
              still deserves the assessment, and a required field here loses
              them before the questions start. */}
          <button className="btn-primary" onClick={() => setPhase('quiz')}>
            {ui.next}
          </button>
        </div>
      </div>
    )
  }

  // ---- questions ---------------------------------------------------------
  if (phase === 'quiz') {
    const cur = flat[step]
    const answered = answers[cur.key] !== undefined
    const last = step === flat.length - 1

    return (
      <div className="mx-auto max-w-2xl sm:max-w-4xl">
        <div className="flex items-baseline justify-between text-sm text-muted-foreground">
          <span>
            {ui.question} {step + 1} / {flat.length}
          </span>
          <span className="font-semibold text-foreground">
            {cur.d.side === 'hub' ? a.ui.hubLabel : a.ui.coreLabel} · {cur.d.title}
          </span>
        </div>
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-border">
          <div
            className="h-full bg-foreground transition-[width] [transition-duration:var(--transition-base)]"
            style={{ width: `${(step / flat.length) * 100}%` }}
          />
        </div>

        {/*
          Every step is laid out in the SAME grid, and the grid has one ROW PER
          PART: dimension framing, then the question, then the options. Each part
          of every step is placed in its own row, so a row is as tall as the
          tallest instance of that part across the run and each part therefore
          starts at the same y on every question — the framing box, the question
          and the first option do not move as the reader clicks through, and
          neither does the Back/Next row.

          One shared cell for the whole step was not enough: it held the BLOCK
          still, but inside it a two-line question pushed the options down and a
          one-line question pulled them back up, which is the movement the
          screenshots were about. Bands fix each part instead of just the total.

          The framing now shows on every question of its dimension rather than
          only the first. It is the context for all three, its row has to be
          reserved either way, and a box that appears and disappears was the
          largest jump on this screen.
        */}
        {/*
          One grid holds the step column and both buttons: from `sm` up they are
          the rails to the left and right of the options, vertically centred, so
          they sit beside the question instead of below the tallest step in the
          run; on a phone there is no room for rails and they drop to a row
          underneath. Same layout in Diagnostic.tsx and GutsCheck.tsx.
        */}
        <div className="mt-8 grid grid-cols-2 items-center gap-4 sm:grid-cols-[auto_1fr_auto] sm:gap-6">
          <div className="col-span-2 row-start-1 grid min-w-0 gap-y-6 sm:col-span-1 sm:col-start-2">
          {flat.map((item, si) => {
            const on = si === step
            return (
            <Fragment key={item.key}>
              <div className={band(on) + ' row-start-1'} inert={on ? undefined : true}>
                <div className="rounded-xl border-l-4 border-l-foreground bg-muted p-5">
                  <p className="text-base font-bold text-foreground">{item.d.title}</p>
                  <p className="mt-1 text-base leading-relaxed text-muted-foreground">
                    {item.d.intro}
                  </p>
                </div>
              </div>

              <div className={band(on) + ' row-start-2'} inert={on ? undefined : true}>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {item.q.component}
                </p>
                <h3 className="mt-2 text-2xl font-bold leading-tight tracking-tight text-foreground">
                  {item.q.text}
                </h3>
              </div>

              <div
                className={band(on) + ' row-start-3 space-y-3'}
                inert={on ? undefined : true}
                // Masked in session replay — see the note in Diagnostic.tsx.
                data-private
              >
                {item.q.opts.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => setAnswers((prev) => ({ ...prev, [item.key]: i }))}
                    className={
                      'flex w-full items-start gap-4 rounded-xl border p-5 text-left text-base leading-relaxed transition-colors [transition-duration:var(--transition-base)] ' +
                      (answers[item.key] === i
                        ? 'border-foreground bg-muted font-medium text-foreground'
                        : 'border-border bg-background text-muted-foreground hover:border-foreground/40')
                    }
                  >
                    <span
                      className={
                        'mt-1 size-4 flex-none rounded-full border-2 ' +
                        (answers[item.key] === i
                          ? 'border-foreground bg-foreground'
                          : 'border-border')
                      }
                    />
                    <span>{opt}</span>
                  </button>
                ))}
              </div>
            </Fragment>
            )
          })}
          </div>

          <button
            className="btn-secondary row-start-2 justify-self-start whitespace-nowrap sm:col-start-1 sm:row-start-1"
            onClick={() => {
              setNeedAnswer(false)
              return step === 0 ? setPhase('context') : setStep((n) => n - 1)
            }}
          >
            {ui.back}
          </button>
          {/*
            Both labels are always in the button, one of them invisible on top of
            the other, so it is as wide as the longer of the two on every
            question. Swapping "Next" for "See the verdict" on the last step
            otherwise widened the right-hand rail and shifted the question column
            sideways at the very end of the run.
          */}
          <button
            className="btn-primary row-start-2 grid justify-items-center justify-self-end whitespace-nowrap sm:col-start-3 sm:row-start-1"
            onClick={() => {
              if (!answered) return setNeedAnswer(true)
              setNeedAnswer(false)
              return last ? setPhase('gate') : setStep((n) => n + 1)
            }}
          >
            <span className={'col-start-1 row-start-1' + (last ? '' : ' invisible')}>
              {ui.seeVerdict}
            </span>
            <span className={'col-start-1 row-start-1' + (last ? ' invisible' : '')}>
              {ui.next}
            </span>
          </button>
        </div>

        {/*
          Pressing Next with nothing chosen used to do nothing at all: the button
          was `disabled`, which reads as broken rather than as "answer first". It
          says so now, and the note clears itself the moment an option is picked.
          Below the buttons rather than above them, so showing it cannot move the
          row it is complaining about.
        */}
        {needAnswer && !answered && (
          <p role="alert" className="mt-4 text-sm font-medium text-destructive">
            {last ? ui.needAnswerLast : ui.needAnswer}
          </p>
        )}
      </div>
    )
  }

  if (phase === 'gate') {
    return (
      <LeadGate
        tool="agentic-readiness-index"
        result={result}
        openedAt={openedAt}
        onDone={(state) => {
          setSaved(state)
          setPhase('report')
          progress.forget()
        }}
      />
    )
  }

  // ---- the report --------------------------------------------------------
  //
  // Routed offerings, from the three weakest dimensions. Deduplicated: two weak
  // dimensions often route to the same offering, and showing it twice reads as a
  // sales sheet rather than a diagnosis.
  const routed = [
    ...new Set(
      scored.lowest
        .map((d) => a.routing[d.id as keyof typeof a.routing])
        .filter((k): k is string => Boolean(k)),
    ),
  ].map((k) => ({
    ...a.solutions[k as keyof typeof a.solutions],
    href: SOLUTION_PAGES[k] as string | undefined,
  }))

  return (
    <div className="mx-auto max-w-3xl">
      {saved.status === 'failed' && (
        <p className="mb-6 rounded-lg border border-border bg-muted p-4 text-sm text-muted-foreground">
          {ui.notSaved}
        </p>
      )}

      <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {a.ui.yourArchetype}
      </p>
      <h3 className="mt-3 text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        {A.name}
      </h3>
      <p className="mt-3 text-xl leading-snug text-muted-foreground">{A.short}</p>

      {/* Hub and Core side by side, with the overall index third. */}
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {[
          { label: a.ui.hubLabel, value: scored.hub },
          { label: a.ui.coreLabel, value: scored.core },
          { label: a.ui.indexLabel, value: scored.index },
        ].map((box) => (
          <div key={box.label} className="rounded-2xl border border-border p-6">
            <p className="text-sm font-semibold text-muted-foreground">{box.label}</p>
            <p className="mt-1 text-4xl font-bold tabular-nums text-foreground">
              {box.value.toFixed(1)}
              <span className="text-lg font-normal text-muted-foreground"> / 5.0</span>
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{rung(box.value).label}</p>
          </div>
        ))}
      </div>

      <p className="mt-8 text-lg leading-relaxed text-muted-foreground">{A.long}</p>
      <div className="mt-6 rounded-2xl border-l-4 border-l-foreground bg-muted p-6">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {a.ui.yourMove}
        </p>
        <p className="mt-2 text-base leading-relaxed text-foreground">{A.move}</p>
      </div>

      {/* Six dimensions, hub then core, each with its ladder rung. */}
      <div className="mt-12 space-y-5">
        {scored.dims.map((d) => (
          <div key={d.id}>
            <div className="flex items-baseline justify-between gap-4">
              <p className="text-base font-semibold text-foreground">
                {d.title}
                <span className="ml-2 text-xs font-normal uppercase tracking-[0.1em] text-muted-foreground">
                  {d.side === 'hub' ? a.ui.hubLabel : a.ui.coreLabel}
                </span>
              </p>
              <p className="flex-none text-sm tabular-nums text-muted-foreground">
                {d.score.toFixed(1)} · {rung(d.score).label}
              </p>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-border">
              <div
                className="h-full rounded-full bg-foreground"
                style={{ width: `${((d.score - 1) / 4) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/*
        The sharpest section, and the reason this tool is worth taking: not "you
        scored low on knowledge" but "this specific component does not exist".
      */}
      {scored.blind.length > 0 && (
        <div className="mt-12">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {a.ui.blindHeading}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{a.ui.blindSub}</p>
          <ul className="mt-4 space-y-2">
            {scored.blind.map((b) => (
              <li key={`${b.dim}-${b.component}`} className="border-l-2 border-border pl-4">
                <p className="text-base font-semibold text-foreground">{b.component}</p>
                <p className="text-sm text-muted-foreground">{b.dim}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {routed.length > 0 && (
        <div className="mt-12">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {a.ui.routedHeading}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{a.ui.routedSub}</p>
          <div className="mt-4 space-y-4">
            {routed.map((s) => (
              <div key={s.nm} className="rounded-2xl border border-border p-6">
                {/* The heading is the way through to the offering's own page. An
                    offering with no page of its own still prints, unlinked. */}
                {s.href ? (
                  <a
                    href={localizeHref(s.href, locale)}
                    className="inline-block text-base font-bold text-foreground underline decoration-border decoration-2 underline-offset-4 transition-colors hover:text-primary hover:decoration-primary"
                  >
                    {s.nm}
                  </a>
                ) : (
                  <p className="text-base font-bold text-foreground">{s.nm}</p>
                )}
                <p className="mt-2 text-base leading-relaxed text-muted-foreground">{s.ds}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-14 border-t border-border pt-10 text-center">
        <p className="text-xl font-semibold leading-snug text-foreground">{a.ui.ctaHeading}</p>
        {/* `#contact` rather than the Contact Us page: the form is at the foot
            of this very page, so the button scrolls to it. */}
        <a className="btn-primary mt-6 inline-flex" href="#contact">
          {ui.ctaLabel}
        </a>
        <p className="mt-6 text-sm text-muted-foreground">
          <button
            className="underline"
            onClick={() => {
              setAnswers({})
              setStep(0)
              setSaved({ status: 'idle' })
              setPhase('intro')
              progress.forget()
            }}
          >
            {ui.restart}
          </button>
        </p>
      </div>
    </div>
  )
}
